import { useCallback, useEffect, useRef, useState } from "react";
import { userRequest } from "../api/requestMethods";
import useVendorAvailabilityUpdates from "./useVendorAvailabilityUpdates";
import {
  availabilityMessage,
  beginRevalidation,
  canOrderFromVendor,
  isScheduledOrdersOnly,
  keepsTradingWhenUnknown,
  normalizeRestaurantAvailability,
  tradingPolicyFor,
} from "../utils/restaurantAvailability";

// ONE place that knows where each vendor type publishes its hours. The backend
// mounts the SAME open-status contract under each base path, so only the prefix
// differs — hardcoding `/restaurants/` here is what made RW, Brand and
// Healthcare hours invisible to shoppers even after the backend shipped.
const OPEN_STATUS_BASE = {
  restaurant: "/restaurants",
  healthcare: "/healthcare",
  retailer: "/rw",
  brand: "/api/brands",
};

// Accept the aliases the rest of the app already passes to useCoverage.
const resolveBase = (vendorType) => {
  const t = String(vendorType || "restaurant").toLowerCase();
  if (t === "rw" || t === "wholesaler" || t === "retail") return OPEN_STATUS_BASE.retailer;
  if (t === "pharmacy" || t === "health") return OPEN_STATUS_BASE.healthcare;
  if (t === "publication") return OPEN_STATUS_BASE.brand;
  // An unknown type (e.g. "inhouse") has no hours endpoint — the caller should
  // pass enabled:false. Fall back to restaurants rather than build a bad URL.
  return OPEN_STATUS_BASE[t] || OPEN_STATUS_BASE.restaurant;
};

const POLL_MS = 60 * 1000;

const useVendorAvailability = ({
  vendorId,
  vendorType = "restaurant",
  outletId = null,
  enabled = true,
}) => {
  const [state, setState] = useState({
    status: enabled && vendorId ? "loading" : "idle",
    availability: null,
    error: null,
    revalidating: false,
  });
  const requestIdRef = useRef(0);
  // Mirrors the backend's `tradingPolicy` payload field. Retail/wholesale, brand
  // and healthcare shops are open by policy until hours are published, so when
  // the hours check itself fails they keep trading (order-create re-checks
  // server-side). Restaurants stay fail-closed.
  const tradingPolicy = tradingPolicyFor(vendorType);
  const tradesWhileUnknown = keepsTradingWhenUnknown(vendorType);

  const refresh = useCallback(
    async ({ silent = false } = {}) => {
      if (!enabled || !vendorId) return null;
      const requestId = ++requestIdRef.current;
      // A re-check is NOT a reset. Flipping `status` back to "loading" here
      // would drop `canOrder` to false for every consumer on the screen — the
      // menu would flash "Checking hours" chips and grey out its CTAs on every
      // add-to-cart. Keep the last settled verdict on screen and only raise a
      // `revalidating` flag; the caller still gates on the awaited return
      // value, so the money path stays fail-closed.
      if (!silent) setState(beginRevalidation);
      try {
        const response = await userRequest.get(
          `${resolveBase(vendorType)}/${vendorId}/open-status`,
          {
            params: {
              ...(outletId ? { outletId } : {}),
              // Cache-buster. Do NOT add a Cache-Control request header here:
              // it is a non-simple header and would trigger a CORS preflight
              // that the API must explicitly allow (see server.js allowedHeaders).
              checkedAt: Date.now(),
            },
          }
        );
        const availability = normalizeRestaurantAvailability(response.data);
        if (!availability) throw new Error("Invalid hours response");
        if (requestId === requestIdRef.current) {
          setState({
            status: "success",
            availability,
            error: null,
            revalidating: false,
          });
        }
        return availability;
      } catch (error) {
        const message =
          error?.response?.data?.message ||
          "We can’t confirm this vendor’s hours right now. Please retry.";
        // State keeps `status: "error"` for every vertical so the banner can say
        // the hours were not confirmed. What the AWAITING caller gets back is
        // policy-dependent: open-until-set shops are told they may proceed.
        if (requestId === requestIdRef.current) {
          setState({
            status: "error",
            availability: null,
            error: message,
            revalidating: false,
          });
        }
        if (tradesWhileUnknown) {
          return {
            available: true,
            code: "AVAILABILITY_UNCONFIRMED",
            message: availabilityMessage(
              { status: "error", error: message },
              vendorType
            ),
            retryable: true,
            tradingPolicy,
          };
        }
        return {
          available: false,
          code: "AVAILABILITY_UNAVAILABLE",
          message,
          retryable: true,
          tradingPolicy,
        };
      }
    },
    [enabled, outletId, tradesWhileUnknown, tradingPolicy, vendorId, vendorType]
  );

  useEffect(() => {
    if (!enabled || !vendorId) {
      setState({
        status: "idle",
        availability: null,
        error: null,
        revalidating: false,
      });
      return undefined;
    }
    refresh();
    const poll = window.setInterval(() => {
      if (document.visibilityState === "visible") refresh({ silent: true });
    }, POLL_MS);
    const recheck = () => refresh({ silent: true });
    window.addEventListener("focus", recheck);
    window.addEventListener("online", recheck);
    document.addEventListener("visibilitychange", recheck);
    return () => {
      requestIdRef.current += 1;
      window.clearInterval(poll);
      window.removeEventListener("focus", recheck);
      window.removeEventListener("online", recheck);
      document.removeEventListener("visibilitychange", recheck);
    };
  }, [enabled, refresh, vendorId, vendorType]);

  useVendorAvailabilityUpdates(
    enabled && vendorId ? { vendorId, vendorType, outletId } : null,
    () => refresh({ silent: true }),
  );

  const canOrder = canOrderFromVendor(state, vendorType);
  // Presentation states. Consumers should branch on these rather than on
  // `!canOrder`, which conflates "closed", "still loading" and "we couldn't
  // reach the API" into one indistinguishable blob. `isChecking`, `isClosed`
  // and `isUnknown` are mutually exclusive; `tradesWhileUnknown` refines
  // `isUnknown` for the unknown-but-trading case (check failed, vertical is open
  // by policy, `canOrder` is true), which deserves a quiet note rather than an
  // amber warning.
  const isChecking = state.status === "loading" || state.status === "idle";
  // Closed now but taking orders for later: `canOrder` stays true, `isClosed`
  // stays false, and checkout forces a scheduled delivery time.
  const scheduledOrdersOnly = isScheduledOrdersOnly(state);
  return {
    ...state,
    canOrder,
    isChecking,
    scheduledOrdersOnly,
    isClosed:
      state.status === "success" &&
      state.availability?.available === false &&
      !scheduledOrdersOnly,
    isUnknown: state.status === "error",
    tradesWhileUnknown: state.status === "error" && tradesWhileUnknown,
    tradingPolicy,
    nextOpening: state.availability?.nextOpening || null,
    message: availabilityMessage(state, vendorType),
    refresh,
  };
};

export default useVendorAvailability;

// Back-compat alias. Every existing restaurant call site keeps working
// unchanged; the default vendorType is "restaurant".
export const useRestaurantAvailability = useVendorAvailability;
